import jsPDF from "jspdf";
import brand_logo from "../../../assets/brand_logo4.png";
import dayjs from "dayjs";

const loadImageAsBase64 = async (url) => {
    try {
        if (!url) return null;

        const res = await fetch(url);
        if (!res.ok) return null;

        const blob = await res.blob();

        return await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        console.error("Image load failed:", error);
        return null;
    }
};

const cropImageCover = (base64, targetW, targetH) =>
    new Promise((resolve) => {
        const img = new Image();

        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = Math.round(targetW * 2);
            canvas.height = Math.round(targetH * 2);

            const ctx = canvas.getContext("2d");

            const srcAspect = img.width / img.height;
            const targetAspect = targetW / targetH;

            let sx = 0;
            let sy = 0;
            let sw = img.width;
            let sh = img.height;

            if (srcAspect > targetAspect) {
                sw = img.height * targetAspect;
                sx = (img.width - sw) / 2;
            } else {
                sh = img.width / targetAspect;
                sy = (img.height - sh) / 2;
            }

            ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL("image/png"));
        };

        img.onerror = () => resolve(base64);
        img.src = base64;
    });

const generateBulkA4PDF = async ({
    bookIds = [],
    rooms = [],
    form = {},
    nights = 0,
    grandTotal = 0,
    paidAmount = 0,
    receivedAdvance = 0,
    remainingPayable = 0,
    discountAmount = 0,
    amountType = null,
    paymentStatus = "",
    loyaltyDiscountPercent = 0,
    loyaltyDiscountAmount = 0,
    gst = 0,
    gstPercent = 0,
    gstAmount = 0,
    gstType = "exclusive",
    headerImg = "",
    footerImg = "",
    hourlyBook,
      totalHours,
      bookingType,
}) => {
    const doc = new jsPDF("p", "mm", "a4");

    const pageW = 210;
    const pageH = 297;
    const m = 10;
    const cW = pageW - m * 2;

    const fitImageByWidth = (base64, maxW) =>
        new Promise((resolve) => {
            const img = new Image();

            img.onload = () => {
                const ratio = img.height / img.width;
                const finalW = maxW;
                const finalH = finalW * ratio;

                resolve({
                    base64,
                    width: finalW,
                    height: finalH,
                });
            };

            img.onerror = () =>
                resolve({
                    base64,
                    width: maxW,
                    height: 20,
                });

            img.src = base64;
        });

    //   const HDR = 20;
    //   const FTR = 10;

    const C = {
        primary: [179, 179, 179],
        accent: [99, 91, 255],
        accentLight: [237, 235, 255],
        success: [22, 101, 52],
        label: [100, 116, 139],
        text: [15, 23, 42],
        muted: [160, 174, 192],
        border: [226, 232, 240],
        cardBg: [248, 250, 252],
        white: [0, 0, 0],
    };

    const fmtRs = (v) =>
        `Rs. ${Math.round(Number(v || 0)).toLocaleString("en-IN")}`;

    const totalPayable = Math.round(Number(grandTotal || 0));
    const paid = Math.round(Number(paidAmount || 0));
    const discount = Math.round(Number(discountAmount || 0));
    const advance = Math.round(Number(receivedAdvance || 0));
    const remaining = Math.round(Number(remainingPayable || 0));

    const isDiscount = amountType === "discount" || discount > 0;
    const isAdvance = amountType === "advance" || advance > 0 || remaining > 0;

    const isFullPaid = isDiscount
        ? paid + discount >= totalPayable
        : remaining <= 0 && paid >= totalPayable;

    const finalStatus =
        paymentStatus ||
        (isDiscount
            ? "Discount Applied"
            : isFullPaid
                ? "Fully Paid"
                : "Advance Received");

    const loyaltyPercent = Number(loyaltyDiscountPercent || 0);
    const loyaltyAmount = Math.round(Number(loyaltyDiscountAmount || 0));

    const finalGstPct =  Number(gstPercent || gst || 0);

    const finalGstAmt =
        finalGstPct > 0
            ? Math.round(
                Number(
                    gstAmount ||
                    totalPayable - totalPayable / (1 + finalGstPct / 100)
                )
            )
            : 0;

    const rawHeader = await loadImageAsBase64(headerImg);
    const rawFooter = await loadImageAsBase64(footerImg);

    const headerImage = rawHeader ? await fitImageByWidth(rawHeader, pageW) : null;
    const footerImage = rawFooter ? await fitImageByWidth(rawFooter, pageW) : null;

    const HDR = headerImage ? headerImage.height : 20;
    const FTR = footerImage ? footerImage.height : 10;

    let y = 0;

    const rgb = (color, type = "text") => {
        if (type === "text") doc.setTextColor(...color);
        if (type === "fill") doc.setFillColor(...color);
        if (type === "draw") doc.setDrawColor(...color);
    };

    const line = (x1, y1, x2, y2, color = C.border) => {
        rgb(color, "draw");
        doc.setLineWidth(0.15);
        doc.line(x1, y1, x2, y2);
    };

    const addFooter = () => {
        const fy = pageH - FTR;

        if (footerImage) {
            doc.addImage(footerImage.base64, "PNG", 0, fy, footerImage.width, footerImage.height);
            return;
        }

        rgb(C.primary, "fill");
        doc.rect(0, fy, pageW, FTR, "F");

        rgb(C.white, "text");
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6);
        doc.text("Thank you for your stay visit again!", pageW / 2, fy + 6, {
            align: "center",
        });
    };

    const addHeader = () => {
        if (headerImage) {
            doc.addImage(headerImage.base64, "PNG", 0, 0, headerImage.width, headerImage.height);
            y = HDR + 2;
        } else {
            rgb(C.primary, "fill");
            doc.rect(0, 0, pageW, HDR, "F");

            try {
                doc.addImage(brand_logo, "PNG", 8, 4, 24, 10);
            } catch { }

            rgb(C.white, "text");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(11);
            doc.text("BOOKING RECEIPT", pageW / 2, 11, {
                align: "center",
            });

            y = HDR + 2;
        }

        line(m, y, pageW - m, y);
        y += 2;
    };

    const checkPage = (needed = 20) => {
        if (y + needed > pageH - FTR - 6) {
            addFooter();
            doc.addPage();
            addHeader();
        }
    };

    const sectionTitle = (title) => {
        checkPage(8);

        // removed background fill
        // rgb(C.accent, "fill");
        // doc.rect(m, y, cW, 5, "F");

        rgb(C.text, "text");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);

        doc.text(String(title).toUpperCase(), m, y + 4);

        // bottom divider line
        line(m, y + 5.5, pageW - m, y + 5.5);

        y += 8;
    };

    const smallPair = (x, label, value, width = 85) => {
        doc.setFontSize(7);
        doc.setFont("helvetica", "bold");
        rgb(C.label, "text");
        doc.text(String(label), x, y);

        doc.setFont("helvetica", "normal");
        rgb(C.text, "text");

        const lines = doc.splitTextToSize(String(value ?? "—"), width - 23);
        doc.text(lines, x + 22, y);

        return Math.max(4.8, lines.length * 4);
    };

    addHeader();

    if (bookIds?.[0]) {
        checkPage(10);

        rgb(C.accentLight, "fill");
        doc.roundedRect(m, y, cW, 7, 1.2, 1.2, "F");

        rgb(C.accent, "text");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.text("Booking Ref:", m + 3, y + 4.8);

        doc.setFont("courier", "bold");
        doc.text(String(bookIds[0]), m + 24, y + 4.8);

        const badgeW = 38;
        const badgeX = pageW - m - badgeW;

        rgb(isFullPaid ? C.success : C.accent, "fill");
        // doc.roundedRect(badgeX, y + 1, badgeW, 5.5, 1.2, 1.2, "F");

        rgb(C.white, "text");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(5.7);
        doc.text(finalStatus.toUpperCase(), badgeX + badgeW / 2, y + 4.8, {
            align: "center",
        });

        y += 9;
    }

    const startY = y;
    const colL = m;
    const colR = m + cW / 2 + 3;
    const colW = cW / 2 - 5;

    sectionTitle("Guest Details");

    const guestStartY = y;

    const guestPairs = [
        ["Name", form?.guest_name || "—"],
        ["Phone", form?.phone || "—"],
        ["Email", form?.email || "—"],
        ["GST No.", form?.customer_gst || "—"],
        ["ID Type", form?.id_type || "—"],
        ["ID No.", form?.id_number || "—"],
    ];

    if (form?.address) guestPairs.push(["Address", form.address]);

    guestPairs.forEach(([label, value]) => {
        const h = smallPair(colL + 1, label, value, colW);
        y += h;
    });

    const afterGuestY = y;

    y = startY;

    sectionTitle(" ");

    const stayPairs = [
        ["Booking Type", bookingType || "—"],
        ["Check-In", form?.check_in || "—"],
        ["Check-Out", form?.check_out || "—"],
        ["Nights", `${nights || 0} night${nights === 1 ? "" : "s"}`],
        ["Guests", form?.guests || "—"],
        ["Pay Mode", form?.payment_mode || "—"],
        // ["Hourly Book", hourlyBook || "—"],
        ["Total Hours", totalHours || "—"],
    ];

    stayPairs.forEach(([label, value]) => {
        const h = smallPair(colR + 1, label, value, colW);
        y += h;
    });

    const afterStayY = y;

    y = Math.max(afterGuestY, afterStayY);

    line(pageW / 2, guestStartY - 7, pageW / 2, y);
    y += 2;
    line(m, y, pageW - m, y);
    y += 3;

    sectionTitle(`Room Details (${rooms.length} ${rooms.length === 1 ? "Room" : "Rooms"})`);

    const cols = [
        { title: "Room", w: 42 },
        { title: "Type", w: 28 },
        { title: "Rate", w: 27 },
        { title: "AC", w: 22 },
        { title: "Extra", w: 31 },
        { title: "Total", w: 40 },
    ];

    let startX = m;

    rgb(C.primary, "fill");
    doc.rect(m, y, cW, 7, "F");

    rgb(C.white, "text");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);

    cols.forEach((col) => {
        doc.text(col.title, startX + 2, y + 4.5);
        startX += col.w;
    });

    y += 7;

    rooms.forEach((roomItem, idx) => {
        checkPage(10);

        const pricePerDay = Number(roomItem?.price_per_day || 0);
        const maxOccupancy = Number(roomItem?.max_occupancy || 0);
        const addCost = Number(roomItem?.add_cost_per_occupancy || 0);
        const acCharge = Number(roomItem?.ac_charge || 0);
        const guests = Number(form?.guests || 1);
        const totalNights = Number(nights || 0);

        const extraGuests = Math.max(0, guests - maxOccupancy);
        const extraTotal = extraGuests * addCost * totalNights;

        const roomTotal =
            pricePerDay * totalNights + extraTotal + acCharge * totalNights;

        const row = [
            `${roomItem?.room_name || "Room"} #${roomItem?.room_number || "—"}`,
            roomItem?.room_type || "—",
            fmtRs(pricePerDay),
            fmtRs(acCharge),
            fmtRs(extraTotal),
            fmtRs(roomTotal),
        ];

        rgb(idx % 2 === 0 ? [248, 250, 252] : [255, 255, 255], "fill");
        doc.rect(m, y, cW, 7, "F");

        rgb(C.text, "text");
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6.5);

        let x = m;

        row.forEach((cell, i) => {
            const text = doc.splitTextToSize(String(cell), cols[i].w - 3);
            doc.text(text[0], x + 2, y + 4.5);
            x += cols[i].w;
        });

        line(m, y + 7, pageW - m, y + 7);
        y += 7;
    });

    y += 3;

    sectionTitle("Payment Summary");

    const payItems = [];

    if (gstType === "inclusive") {
        if (finalGstPct > 0) {
            const half = finalGstAmt / 2;
            payItems.push([`CGST (${finalGstPct / 2}%)`, fmtRs(half)]);
            payItems.push([`SGST (${finalGstPct / 2}%)`, fmtRs(half)]);
        }
    } else if (gstType === "exclusive" && finalGstPct > 0) {
        payItems.push([`GST (${finalGstPct}%) Included`, ""]);
    }

    if (loyaltyPercent > 0) {
        payItems.push([`Loyalty Discount (${loyaltyPercent}%)`, `- ${fmtRs(loyaltyAmount)}`]);
    }

    payItems.push(["Total Payable", fmtRs(totalPayable)]);
    payItems.push(["Amount Paid", fmtRs(paid)]);

    if (isDiscount) {
        payItems.push(["Discount", `- ${fmtRs(discount)}`]);
    }

    if (isAdvance && !isDiscount) {
        payItems.push(["Advance", fmtRs(advance || paid)]);
        payItems.push(["Remaining", fmtRs(remaining)]);
    }

    checkPage(payItems.length * 6 + 16);

    payItems.forEach(([lbl, val], i) => {
        const rowY = y + i * 6;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        rgb(C.label, "text");
        doc.text(lbl, m + 2, rowY);

        doc.setFont("helvetica", "bold");
        rgb(C.text, "text");
        doc.text(val, pageW - m - 2, rowY, {
            align: "right",
        });
    });

    y += payItems.length * 6 + 3;

    checkPage(14);

    rgb(C.primary, "fill");
    doc.rect(m, y, cW, 10, "F");

    rgb(C.white, "text");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("GRAND TOTAL", m + 3, y + 6);

    doc.setFontSize(10);
    doc.text(fmtRs(totalPayable- discount ), pageW - m - 3, y + 6, {
        align: "right",
    });

    y += 12;

    const printed = new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });

    // rgb(C.muted, "text");
    // doc.setFont("helvetica", "normal");
    // doc.setFontSize(6.5);
    // doc.text(`Generated: ${printed}`, m, y);

    addFooter();
    const now = dayjs().format("DD-MM-YYYY-hh-mm-A");

    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);

    // Open in new tab
    window.open(pdfUrl, "_blank");

    // Auto download
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `hotel-room-bulk-bill-${now}.pdf`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Cleanup
    setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
};

export default generateBulkA4PDF;